import { readerTypes, defaultIdentifierType } from '@/constants/constants';
import ApiClient from '@/services/apiClient';
import Parity from '@/models/parity';
import Range from '@/models/range';
import BluetoothConfig from './bluetoothConfig';

export default class ReaderFormat {
    format_type;   //"WAuto", "WCustom", "CDAuto", "CDCustom", "SSCP", "OSDP"
    size
    parities;
    card_number;
    facility_code;
    encoding;   //"Hex", "Decimal"
    format_id;
    facility_codes;
    bus_address;
    use_default_key;
    communication_key;
    tamper;
    show_bluetooth_config;
    bluetooth_config;
    /** Type given to the decoded identifiers ('unknown' = any type) */
    identifier_type;
    /** Number of keypad digits completing a code entry, null when the entry is completed by '#' */
    keypad_code_length;
    /** OSDP only : detect Haxo secure QR codes in the reader's ASCII reads */
    haxo_scan;

    constructor() {
      this.format_type = "WAuto";
      this.size = 16;
      this.parities = [new Parity(), new Parity()];
      this.card_number = new Range();
      this.facility_code = new Range();
      this.encoding = "Hex";
      this.format_id = 3;
      this.facility_codes = [];
      this.bus_address = 0;
      this.use_default_key = true;
      this.communication_key = "";
      this.tamper = "Inactive";
      this.show_bluetooth_config = false;
      this.bluetooth_config = new BluetoothConfig();
      this.identifier_type = "unknown";
      this.keypad_code_length = null;
      this.haxo_scan = false;
    }

    /** Default format of a new installation : HAXO SECURE (same as haxoconf DefaultReaderDefaultConfiguration) */
    static haxoSecure() {
      const format = new ReaderFormat();
      format.format_id = 13;
      format.format_type = 'OSDP';
      format.haxo_scan = true;
      return format;
    }

    static constructFromObject(data, obj) {
      obj = obj || new ReaderFormat();
      if (data) {
        const key = ApiClient.convertToType(data['communication_key'], 'String') ?? '';
        const default_key = key === "default_0" || ApiClient.convertToType(data['use_default_key'], 'Boolean') === true;

        obj.format_type = ApiClient.convertToType(data['format_type'], 'String');
        obj.size = ApiClient.convertToType(data['size'], 'Integer');
        if (data.parities.length > 0) {
          obj.parities = ApiClient.convertToType(data.parities, [Parity]);
        }
        obj.card_number = Range.constructFromObject(data['card_number']);
        obj.facility_code = Range.constructFromObject(data['facility_code']);
        obj.encoding = ApiClient.convertToType(data['encoding'], 'String');
        obj.format_id = ApiClient.convertToType(data['format_id'], 'Integer');
        obj.facility_codes = ApiClient.convertToType(data['facility_codes'], ['Integer']) ?? [];
        obj.bus_address = ApiClient.convertToType(data['bus_address'], 'Integer')?? 0;
        obj.use_default_key = default_key;
        obj.communication_key = default_key ? "" : key;
        obj.tamper = ApiClient.convertToType(data['tamper'], 'String')?? 'Inactive';
        obj.show_bluetooth_config = ApiClient.convertToType(data['show_bluetooth_config'], 'Boolean')?? ('bluetooth_config' in data);
        obj.bluetooth_config = BluetoothConfig.constructFromObject(data['bluetooth_config']);
        obj.identifier_type = ApiClient.convertToType(data['identifier_type'], 'String')?? defaultIdentifierType(obj.format_id);
        obj.keypad_code_length = ApiClient.convertToType(data['keypad_code_length'], 'Integer')?? null;
        obj.haxo_scan = ApiClient.convertToType(data['haxo_scan'], 'Boolean')?? false;
      }
      return obj;
    }

    toJSON() {
      const json = {
        format_type: this.format_type,
        size: this.size,
        parities: this.parities.filter(p => p.enable),
        encoding: this.encoding,
        format_id: this.format_id,
      };
      if(this.card_number.enable) {
        json.card_number = this.card_number;
      }
      if(this.facility_code.enable) {
        json.facility_code = this.facility_code;
        json.facility_codes = this.facility_codes;
      }
      // Chosen by the installer, or imposed by the reader type
      if(this.has_identifier_type || this.identifier_type !== 'unknown') {
        json.identifier_type = this.identifier_type;
      }
      // Absent : the keypad entry is completed by '#'
      if((this.has_keypad || this.identifier_type === 'code') && this.keypad_code_length != null) {
        json.keypad_code_length = this.keypad_code_length;
      }
      if(this.is_rs485) {
        json.bus_address = this.bus_address;
        json.communication_key = this.use_default_key? "default_0" : this.communication_key;
        json.tamper = this.tamper;
        if (this.show_bluetooth_config) {
          json.bluetooth_config = this.bluetooth_config;
        }
        if (this.haxo_scan) {
          json.haxo_scan = true;
        }
      }
      return json;
    }

    // Same rules as ReaderType.getLabel in haxoconf
    toString() {
      const type = this.readerType;
      if(!type) {
        return 'format inconnu';
      }
      if(type.is_rs485) {
        return `${type.label} (adresse ${this.bus_address})`;
      }
      if(type.config || type.dataType == null) {
        return type.label;
      }
      return `${type.label} (${this.size} ${type.dataType})`;
    }

    get readerType() {
      return readerTypes.find(type => type.formatId === this.format_id);
    }

    /** True when the reader may embed a keypad whose entry completion is configurable */
    get has_keypad() {
      return this.readerType?.hasKeypad === true;
    }

    /** True when the installer can choose the type of the identifiers read */
    get has_identifier_type() {
      return this.readerType?.hasIdentifierType === true;
    }

    get is_rs485() {
      switch(this.format_type) {
        case "SSCP":
        case "OSDP":
          return true;
        default:
          return false;
      }
    }
}
